/**
 * The acknowledgements a person owes, and the act of clearing one.
 *
 * QUERIES ARE SHAPED BY THE RULES, as everywhere else in this app.
 * firestore.rules grants reads on acknowledgements through:
 *
 *     allow read: if sameOrg() && resource.data.userId == request.auth.uid
 *     allow read: if sameOrg() && isOrgAdmin()
 *
 * so a list has to be constrained by `userId == me` for anyone who is not an
 * administrator — Firestore cannot prove an unconstrained query would only
 * return readable documents, and denies it outright. Constraining by userId
 * is also simply correct here: this is "what I owe", not "what everyone owes".
 */

import { calendarDate } from '../../shared/dates';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { collection, getDocs, getFirestore, query, where } from 'firebase/firestore';
import { app } from '../../lib/firebase';
import { call } from '../../lib/callable';

const db = getFirestore(app);

/**
 * EVERY STATUS THE BACKEND WRITES, NAMED, PLUS ONE FOR ANYTHING ELSE.
 *
 * This used to be four values with a default of `pending` for everything it
 * did not recognize, and `cancelled` was everything it did not recognize. A
 * requirement the server had closed months earlier — because the version was
 * superseded, the policy retired, or the person removed and brought back —
 * therefore read as an open task, sat on "Your tasks" with a live button
 * forever, and produced a refusal from the server when pressed. Finding R4.
 *
 * `unknown` is the fail-closed value. A status nothing writes today is a
 * status whose meaning nobody has decided, and the safe reading of an
 * undecided state is "do not offer an attestation for it".
 */
export type AckStatus = 'pending' | 'overdue' | 'acknowledged' | 'waived' | 'cancelled' | 'unknown';

/** The statuses a person can still act on. Everything else is terminal. */
const ACTIONABLE: readonly AckStatus[] = ['pending', 'overdue'];

/** What the server writes, mapped one by one rather than by a default. */
const STORED: Record<string, AckStatus> = {
  pending: 'pending',
  overdue: 'overdue',
  acknowledged: 'acknowledged',
  waived: 'waived',
  cancelled: 'cancelled',
};

/** Why a requirement was closed, in the words a reader would use. */
export function closedBecause(reason: string | null): string {
  switch (reason) {
    case 'version_superseded': return 'A newer version has been published since you were asked to read this one.';
    case 'policy_retired': return 'This policy has been retired.';
    case 'member_removed': return 'This closed when your membership changed.';
    default: return 'It is no longer required.';
  }
}

export interface Acknowledgement {
  id: string;
  policyId: string;
  policyVersionId: string;
  policyNumber: string;
  policyTitle: string;
  /** The version this requirement names, which is the one an attestation binds to. */
  versionNumber: number | null;
  status: AckStatus;
  /** ISO date. Null when no deadline was set. */
  dueDate: string | null;
  acknowledgedAt: string | null;
  /** Only on a cancelled requirement, and only when the server recorded one. */
  cancelledReason: string | null;
}

/** A date-only value, read in UTC. One rule, in shared/dates.ts. */
const toIsoDate = calendarDate;

function toStatus(v: unknown): AckStatus {
  return typeof v === 'string' && STORED[v] ? STORED[v] : 'unknown';
}

function toAck(id: string, d: Record<string, unknown>): Acknowledgement {
  return {
    id,
    policyId: typeof d.policyId === 'string' ? d.policyId : '',
    policyVersionId: typeof d.policyVersionId === 'string' ? d.policyVersionId : '',
    policyNumber: typeof d.policyNumber === 'string' ? d.policyNumber : '',
    policyTitle: typeof d.policyTitle === 'string' ? d.policyTitle : '(untitled policy)',
    versionNumber: typeof d.versionNumber === 'number' ? d.versionNumber : null,
    status: toStatus(d.status),
    dueDate: toIsoDate(d.dueDate),
    acknowledgedAt: toIsoDate(d.acknowledgedAt),
    cancelledReason: typeof d.cancelledReason === 'string' && d.cancelledReason ? d.cancelledReason : null,
  };
}

/**
 * Everything this person has been asked to acknowledge.
 *
 * Returned whole rather than filtered to the outstanding ones, because the
 * completed rows are what somebody points at when asked to prove they read a
 * policy — which is the record being sold.
 */
export function useMyAcknowledgements(orgId: string | null, uid: string | null) {
  return useQuery({
    queryKey: ['acknowledgements', orgId, uid],
    enabled: Boolean(orgId && uid),
    /**
     * ALWAYS RE-READ ON ARRIVAL. The app's default is a minute of freshness,
     * which is right for a library of six hundred policies and wrong for the
     * one list that decides whether a button is offered: a version published
     * while somebody had the tab open would leave a live "Acknowledge" on a
     * requirement the server has already closed. This is one indexed query for
     * one person's own rows.
     */
    staleTime: 0,
    refetchOnMount: 'always',
    queryFn: async (): Promise<Acknowledgement[]> => {
      const snap = await getDocs(
        query(
          collection(db, 'orgs', orgId!, 'acknowledgements'),
          where('userId', '==', uid!),
        ),
      );
      return snap.docs.map((d) => toAck(d.id, d.data()));
    },
  });
}

/** Outstanding first, soonest deadline first, undated last. */
export function sortByUrgency(acks: readonly Acknowledgement[]): Acknowledgement[] {
  const rank = (a: Acknowledgement) =>
    a.status === 'overdue' ? 0 : a.status === 'pending' ? 1 : 2;

  return [...acks].sort((a, b) => {
    const byRank = rank(a) - rank(b);
    if (byRank !== 0) return byRank;

    // Undated last within a group, for the same reason it is last in the
    // policy list: missing is not "soonest".
    if (!a.dueDate && !b.dueDate) return 0;
    if (!a.dueDate) return 1;
    if (!b.dueDate) return -1;
    return a.dueDate.localeCompare(b.dueDate);
  });
}

export function isOutstanding(a: Acknowledgement): boolean {
  return ACTIONABLE.includes(a.status);
}

/** Closed without anybody reading anything, or in a state nobody can act on. */
export function isClosed(a: Acknowledgement): boolean {
  return a.status === 'cancelled' || a.status === 'unknown';
}

/** Given, or excused. The record somebody points at. */
export function isSettled(a: Acknowledgement): boolean {
  return a.status === 'acknowledged' || a.status === 'waived';
}

/**
 * The requirement this person holds for one version of one policy, if any.
 *
 * The policy page asks this to decide whether to offer the attestation. It
 * reads the list already loaded for "Your tasks" rather than querying again,
 * and it matches on the VERSION as well as the policy, so a requirement for a
 * version that has since been replaced is not mistaken for a live one.
 */
export function requirementFor(
  acks: readonly Acknowledgement[] | undefined,
  policyId: string | null | undefined,
  versionId: string | null | undefined,
): Acknowledgement | null {
  if (!acks || !policyId || !versionId) return null;
  return acks.find((a) => a.policyId === policyId && a.policyVersionId === versionId) ?? null;
}

/**
 * Record an acknowledgement.
 *
 * Through the callable, not a direct write: the rules refuse client writes to
 * this collection outright, and the function does more than set a flag — it
 * checks the policy is published, stamps the version actually acknowledged,
 * and writes the audit entry that makes the record worth anything.
 */
export function useAcknowledge(orgId: string | null, uid: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { policyId: string; policyVersionId: string }) =>
      call<typeof input, { success: boolean }>('recordAcknowledgement', input),
    onSettled: () => {
      /**
       * On SETTLED, not on success. A refusal is the other half of this: the
       * server turns one down because the version has moved on or the
       * requirement was closed, and the page has to be re-read so it stops
       * offering the button as well as showing the message.
       */
      void queryClient.invalidateQueries({ queryKey: ['acknowledgements', orgId, uid] });
    },
  });
}
